import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  safeGetPersistedValue,
  safePersistValue,
} from '@lifeomic/phc-web-toolkit/src/hooks/useLocalStorageState';

import { Conversations } from '../graphql/queries';
import { ConversationsConnection } from '../__generated__/api.graphql';

export const ZORI_RECENT_CONVERSATIONS_KEY = 'zoriRecentConversationsV4';
export const ZORI_HIDDEN_CONVERSATIONS_KEY = 'zoriHiddenConversationsV1';

export type RecentConversationStorageEntry = {
  conversationId: string;
  patientId?: string;
  title?: string;
  lastOpenedAt?: string;
  userIds?: string[];
};

export type RecentConversation = RecentConversationStorageEntry & {
  key: string;
  latestMessageText?: string;
  latestMessageTime?: string;
  latestMessageUserId?: string;
  hasUnread?: boolean;
};

type ConversationsQueryResponse = {
  conversations: ConversationsConnection;
};

const normalizeHiddenValue = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter(
    entry => typeof entry === 'string' && entry.trim().length > 0,
  );
};

export const getRecentConversationKey = (
  entry: RecentConversationStorageEntry,
) => {
  if (entry.conversationId) return entry.conversationId;
  if (entry.userIds?.length) return [...entry.userIds].sort().join('|');
  if (entry.patientId) return entry.patientId;
  return '';
};

export const persistRecentConversation = (
  entry: RecentConversationStorageEntry,
  maxEntries = 10,
) => {
  const key = getRecentConversationKey(entry);
  if (!key) return;

  const existing =
    safeGetPersistedValue<RecentConversationStorageEntry[]>(
      ZORI_RECENT_CONVERSATIONS_KEY,
    ) || [];

  const filtered = existing.filter(
    existingEntry => getRecentConversationKey(existingEntry) !== key,
  );

  safePersistValue(
    ZORI_RECENT_CONVERSATIONS_KEY,
    [entry, ...filtered].slice(0, maxEntries),
  );
};

export const removeRecentConversation = (
  entry: RecentConversationStorageEntry,
) => {
  const key = getRecentConversationKey(entry);
  if (!key) return;
  const existing =
    safeGetPersistedValue<RecentConversationStorageEntry[]>(
      ZORI_RECENT_CONVERSATIONS_KEY,
    ) || [];
  const filtered = existing.filter(
    existingEntry => getRecentConversationKey(existingEntry) !== key,
  );
  safePersistValue(ZORI_RECENT_CONVERSATIONS_KEY, filtered);
};

export const useRecentConversations = () => {
  const { data, loading } = useQuery<ConversationsQueryResponse>(
    Conversations,
    {
      variables: { first: 10 },
    },
  );
  const [hiddenConversationIds, setHiddenConversationIds] = useState(() => {
    const stored =
      safeGetPersistedValue<string[]>(ZORI_HIDDEN_CONVERSATIONS_KEY) || [];
    return normalizeHiddenValue(stored);
  });
  const recentConversations = useMemo<RecentConversation[]>(() => {
    if (!data?.conversations?.edges?.length) return [];
    return data.conversations.edges
      .map(edge => edge.node)
      .filter(Boolean)
      .filter(
        conversation =>
          !hiddenConversationIds.includes(conversation.conversationId),
      )
      .map(conversation => {
        const patientIdFromScope = conversation.userIds
          ?.find(id => id.startsWith('scope?subject='))
          ?.split('scope?subject=')[1]
          ?.trim();

        return {
          conversationId: conversation.conversationId,
          userIds: conversation.userIds,
          latestMessageText: conversation.latestMessageText,
          latestMessageTime: conversation.latestMessageTime,
          latestMessageUserId: conversation.latestMessageUserId,
          hasUnread: conversation.hasUnread,
          patientId: patientIdFromScope,
          key: conversation.conversationId,
        };
      });
  }, [data, hiddenConversationIds]);

  const hideConversation = (conversationId?: string) => {
    if (!conversationId) return;
    setHiddenConversationIds(prev => {
      if (prev.includes(conversationId)) return prev;
      const next = [...prev, conversationId];
      safePersistValue(ZORI_HIDDEN_CONVERSATIONS_KEY, next);
      return next;
    });
  };

  const unhideConversation = (conversationId?: string) => {
    if (!conversationId) return;
    setHiddenConversationIds(prev => {
      if (!prev.includes(conversationId)) return prev;
      const next = prev.filter(id => id !== conversationId);
      safePersistValue(ZORI_HIDDEN_CONVERSATIONS_KEY, next);
      return next;
    });
  };

  return {
    recentConversations,
    loading,
    hideConversation,
    unhideConversation,
  };
};
